import { IMetadata } from 'common/defines/clients';
import { ReactNode } from 'react';

export interface ITreeNode {
  _id: string;
  name: string;
  parent: string | null;
  isLastLevel: boolean;
  children: ITreeNode[];
}

export interface ITreeNodeCheckbox extends ITreeNode {
  isCheck: boolean;
  children: ITreeNodeCheckbox[] | [];
}

export enum TransactionModeEnum {
  UNLINKED = 'UNLINKED',
  LINKED = 'LINKED',
}

export interface Link {
  _id: string;
  harvestId: string;
  tag: string;
  weighbridgeId: string;
  weighbridgeNetWeight: number;
}

export interface IHarvestResponseData {
  _id: string;
  analysisId: string;
  hvDate: Date;
  hvId: string;
  estateName: string;
  divisionName: string;
  blockName: string;
  inputBy: string;
  workerId: string;
  hvRipe: number;
  hvOverripe: number;
  hvUnripe: number;
  hvUnderripe: number;
  hvRotten: number;
  hvLongstalk: number;
  hvEmpty: number;
  hvRatdamage: number;
  hvTotal: number;
  hvLoosefr: number;
  paidBlock: number;
  driverName: string;
  vehicleNumber: string;
  groupName: string;
  linkStatus: string;
  links: Link[];
}

export interface ILinkedHarvestTableData extends IHarvestResponseData {
  paidFFB: number;
  averageWeight: number;
  totalWeight: number;
}

export interface ISummaryHarvestByWeighbridgeId {
  weighbridgeId: string;
  totalFFB: number;
}

export interface IHarvestTransactionResponse {
  docs: IHarvestResponseData[];
  metadata: IMetadata;
  summaryHarvestByWeighbridgeId: ISummaryHarvestByWeighbridgeId[];
}

export interface IHarvestResponse {
  levelId: string;
  data: IHarvestResponseData[];
}

export enum FormulaModeEnum {
  SUM = 'SUM',
  LIST = 'LIST',
  NONE = 'NONE',
}

export interface ITreeNodeCheckboxHarvest extends ITreeNodeCheckbox {
  harvestData: IHarvestResponseData[];
  children: ITreeNodeCheckboxHarvest[];
  formulaMode?: FormulaModeEnum;
}

export interface IFilterDate {
  startDate: string;
  endDate: string;
}

export interface IWeighbridgeTransactionTableResponse {
  _id: string;
  analysisId: string;
  weighDate: Date;
  timestamp: string;
  weighbridgeLocation: string;
  weighTransaction: string;
  vehicleDetails: string;
  lorryWeight: number;
  harvestWeight: number;
  netWeight: number;
  driverName: string;
}

export interface IWeighbridgeTransactionTableDisplay extends IWeighbridgeTransactionTableResponse {
  tags: string;
}

export interface IWeighbridgeFormInput
  extends Pick<
    IWeighbridgeTransactionTableResponse,
    'weighbridgeLocation' | 'weighTransaction' | 'vehicleDetails' | 'driverName' | 'timestamp'
  > {
  weighDate: string;
  lorryWeight: string;
  harvestWeight: string;
  netWeight: string;
}
export interface IOption {
  label: string;
  value: string;
}

export enum LinkStatusEnum {
  LINK = 'LINK',
  UNLINK = 'UNLINK',
}

export interface IUnlinkHarvestTransactionMutateData {
  fromDate: string;
  toDate: string;
  currentPage: number;
  levelIds: string[];
  linkStatus: LinkStatusEnum;
  perPage: number;
}

export type IWeighbridgeTransactionMutateData = Omit<IUnlinkHarvestTransactionMutateData, 'linkStatus'>;

export interface ICreateHarvestLinkRequest {
  harvestId: string;
  tag: string;
  weighbridgeId: string;
}
export type IWeighbridgeTransactionDistributeData = Pick<IUnlinkHarvestTransactionMutateData, 'levelIds'>;

export interface IErmsSelectCard {
  key: number;
  value: ErmsCardEnum;
  label: string;
  avatar: ReactNode;
}

export enum ErmsCardEnum {
  HARVEST = 'HARVEST',
  PRODUCTION = 'PRODUCTION',
  WEIGHBRIDGE = 'WEIGHBRIDGE',
}

export enum WeightUnitEnum {
  METRIC_TONNE = 'METRIC_TONNE',
  KILOGRAM = 'KILOGRAM', // yield
  KILOGRAM_PER_HECTARE = 'KILOGRAM_PER_HECTARE',
  METRIC_TONNE_PER_HECTARE = 'METRIC_TONNE_PER_HECTARE',
  KILOGRAM_PER_ACRE = 'KILOGRAM_PER_ACRE',
  METRIC_TONNE_PER_ACRE = 'METRIC_TONNE_PER_ACRE',
  KILOGRAM_PER_TREE = 'KILOGRAM_PER_TREE',
  METRIC_TONNE_PER_TREE = 'METRIC_TONNE_PER_TREE',
  KILOGRAM_PER_BUNCH = 'KILOGRAM_PER_BUNCH', // yield
  METRIC_TONNE_PER_HARVEST = 'METRIC_TONNE_PER_HARVEST', //loose fruit
  KILOGRAM_PER_HARVEST = 'KILOGRAM_PER_HARVEST', //loose fruit
}

export interface IYieldSummary {
  totalHvOverripe: number;
  totalHvRipe: number;
  totalHvUnderripe: number;
  totalWeighbridgeNetWeight: number;
  totalHarvest: number;
  _id: string;
}

export interface IFruitGradeData {
  _id: string;
  totalHvRipe: number;
  totalHvOverripe: number;
  totalHvUnderripe: number;
  totalHvUnripe: number;
  totalHvRotten: number;
  totalHvLongstalk: number;
  totalHvEmpty: number;
  totalHvRatdamage: number;
  hvAbnormalbunches: number;
}

export interface ILooseFruitData {
  totalHarvest: number;
  totalHvLoosefr: number;
  _id: string;
}
